// src/pages/MessagesPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Lock, Heart, Megaphone, Users } from 'lucide-react';
import conversationService from '../services/conversationService';
import userService from '../services/userService';
import { ConversationItem } from '../components/chat';
import { Btn, Card, EmptyState, ErrorState } from '../components/ui';
import { canAccessDating } from '../utils';

const MessagesPage = ({ user, openAuth }) => {
  const [conversations, setConversations] = useState([]);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const profileCache = useRef({});
  const blockedRef = useRef(user?.blocked || []);
  blockedRef.current = user?.blocked || [];

  // Abonnement temps réel aux conversations
  useEffect(() => {
    if (!user?.uid) return;

    const unsub = conversationService.subscribeToConversations(
      user.uid,
      async ({ conversations: convs }) => {
        try {
          // Filtrer les conversations avec utilisateurs bloqués
          const userBlocked = blockedRef.current;
          const filteredConvs = convs.filter((conv) => {
            const otherUid = conv.participants.find((p) => p !== user.uid);
            return !userBlocked.includes(otherUid);
          });
          setConversations(filteredConvs);

          // Charger les profils des autres participants (avec cache)
          const uidsToLoad = [];
          for (const conv of convs) {
            const otherUid = conv.participants.find((p) => p !== user.uid);
            if (otherUid && !profileCache.current[otherUid]) {
              uidsToLoad.push(otherUid);
            }
          }

          if (uidsToLoad.length > 0) {
            const loaded = await Promise.all(uidsToLoad.map((uid) => userService.getProfile(uid)));
            const newProfiles = { ...profileCache.current };
            loaded.forEach((profile, i) => {
              if (profile) newProfiles[uidsToLoad[i]] = profile;
            });
            profileCache.current = newProfiles;
            setProfiles({ ...newProfiles });
          }

          setError(false);
        } catch (err) {
          console.error('MessagesPage load error:', err);
          setError(true);
        } finally {
          setLoading(false);
        }
      }
    );

    return unsub;
  }, [user?.uid]);

  const handleConversationClick = async (conv) => {
    // Marquer comme lue
    if (conv.lastMessageSenderId && conv.lastMessageSenderId !== user.uid) {
      const userReadAt = conv.readBy?.[user.uid];
      const lastMsgTime = conv.lastMessageAt?.toMillis?.() || 0;
      const readTime = userReadAt?.toMillis?.() || 0;
      if (!userReadAt || lastMsgTime > readTime) {
        await conversationService.markAsRead(conv.id, user.uid);
      }
    }

    navigate(`/messages/${conv.id}`);
  };

  // Non connecté
  if (!user) {
    return (
      <div className="pt-32 pb-32 container mx-auto px-4">
        <Card className="max-w-md mx-auto p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center mx-auto mb-4">
            <Lock size={28} className="text-rose-500" />
          </div>
          <h2 className="text-2xl font-bold mb-3 dark:text-gray-100">
            {t('messages.guest.title')}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6">{t('messages.guest.desc')}</p>
          <Btn onClick={openAuth} className="w-full" data-testid="messages-btn-auth">
            {t('messages.guest.cta')}
          </Btn>
        </Card>
      </div>
    );
  }

  const friendCount = conversations.filter((c) => c.type === 'friend').length;
  const datingCount = conversations.filter((c) => !c.type || c.type === 'match').length;
  const annonceCount = conversations.filter((c) => c.type === 'annonce').length;

  const filteredConversations =
    activeFilter === 'all'
      ? conversations
      : activeFilter === 'friend'
        ? conversations.filter((c) => c.type === 'friend')
        : activeFilter === 'dating'
          ? conversations.filter((c) => !c.type || c.type === 'match')
          : conversations.filter((c) => c.type === 'annonce');

  const TAB_COLORS = {
    all: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    friend: 'bg-violet-100 text-violet-700 dark:bg-violet-950/30 dark:text-violet-400',
    dating: 'bg-rose-100 text-rose-700 dark:bg-rose-950/30 dark:text-rose-400',
    annonces: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/30 dark:text-indigo-400',
  };

  const tabs = [
    { key: 'all', lk: 'messages.tabs.all', icon: MessageCircle, count: conversations.length },
    { key: 'friend', lk: 'messages.tabs.friends', icon: Users, count: friendCount },
    { key: 'dating', lk: 'messages.tabs.dating', icon: Heart, count: datingCount },
    { key: 'annonces', lk: 'messages.tabs.annonces', icon: Megaphone, count: annonceCount },
  ];

  return (
    <div className="max-w-2xl mx-auto px-4 pt-24 pb-32 lg:pb-8">
      <Helmet>
        <title>{t('messages.meta.title')}</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://hmonghearts.com/messages" />
      </Helmet>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {t('messages.title')}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {conversations.length > 0
            ? conversations.length > 1
              ? t('messages.conversationCount_plural', { count: conversations.length })
              : t('messages.conversationCount', { count: conversations.length })
            : t('messages.emptySubtitle')}
        </p>
      </div>

      {/* Onglets filtres */}
      {!loading && !error && conversations.length > 0 && (
        <div
          className="flex gap-2 mb-4 overflow-x-auto pb-1 scrollbar-hide"
          data-testid="messages-filter-tabs"
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeFilter === tab.key;
            return (
              <button
                key={tab.key}
                data-testid={`messages-filter-${tab.key}`}
                onClick={() => setActiveFilter(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full transition whitespace-nowrap flex-shrink-0 ${
                  isActive
                    ? TAB_COLORS[tab.key]
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <Icon size={14} />
                {t(tab.lk)} ({tab.count})
              </button>
            );
          })}
        </div>
      )}

      {/* Contenu */}
      {error ? (
        <ErrorState onRetry={() => window.location.reload()} />
      ) : loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-3 p-3 sm:p-4 rounded-xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 animate-pulse"
            >
              <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded-lg" />
                <div className="h-3 w-48 bg-gray-200 dark:bg-gray-700 rounded-lg" />
              </div>
              <div className="h-3 w-10 bg-gray-200 dark:bg-gray-700 rounded-lg flex-shrink-0" />
            </div>
          ))}
        </div>
      ) : conversations.length === 0 ? (
        <div className="text-center py-16" data-testid="messages-empty-state">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-rose-100 to-fuchsia-100 dark:from-rose-900/30 dark:to-fuchsia-900/30 flex items-center justify-center mx-auto mb-5">
            <MessageCircle size={32} className="text-rose-500 dark:text-rose-400" />
          </div>
          <h3 className="text-xl font-bold mb-2 dark:text-gray-100">{t('messages.empty.title')}</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
            {canAccessDating(user)
              ? t('messages.empty.descDating')
              : t('messages.empty.descNoDating')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Btn onClick={() => navigate('/members')} data-testid="messages-empty-cta-members">
              {t('messages.empty.ctaMembers')}
            </Btn>
            {canAccessDating(user) && (
              <Btn
                v="outline"
                onClick={() => navigate('/discover')}
                data-testid="messages-empty-cta-discover"
              >
                {t('messages.empty.ctaDiscover')}
              </Btn>
            )}
            <Btn
              v="outline"
              onClick={() => navigate('/annonces')}
              data-testid="messages-empty-cta-annonces"
            >
              {t('messages.empty.ctaAnnonces')}
            </Btn>
          </div>
        </div>
      ) : filteredConversations.length === 0 ? (
        activeFilter === 'friend' ? (
          <EmptyState
            icon={Users}
            title={t('messages.emptyFriends.title')}
            desc={t('messages.emptyFriends.desc')}
          />
        ) : activeFilter === 'dating' ? (
          <EmptyState
            icon={Heart}
            title={t('messages.emptyDating.title')}
            desc={t('messages.emptyDating.desc')}
          />
        ) : (
          <EmptyState
            icon={Megaphone}
            title={t('messages.emptyAnnonces.title')}
            desc={t('messages.emptyAnnonces.desc')}
          />
        )
      ) : (
        <div className="space-y-2">
          {filteredConversations.map((conv) => {
            const otherUid = conv.participants.find((p) => p !== user.uid);
            return (
              <ConversationItem
                key={conv.id}
                conversation={conv}
                otherUser={profiles[otherUid] || null}
                currentUserId={user.uid}
                onClick={() => handleConversationClick(conv)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
